/**
 * AppLayout Component
 *
 * Main application layout using SmartFace SqwLayout
 * Provides header with breadcrumbs, sidebar navigation, and content area
 */

import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { Breadcrumb } from '@hrworks/sui-core/Breadcrumb'
import { BreadcrumbItem } from '@hrworks/sui-core/Breadcrumb/BreadcrumbItem'
import { CollapsibleMenu, CollapsibleMenuEntry } from '@hrworks/sui-core/CollapsibleMenu'
import { StreamlineIcon } from '@hrworks/sui-core/StreamlineIcon'
import { SqwLayout } from '@hrworks/sui-extension/SqwLayout'

import { ROUTES } from '../../../routes/routes'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { UserMenu } from '../UserMenu'

export const AppLayout = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const breadcrumbs = useBreadcrumbs()
  const { formatMessage } = useIntl()

  /** Navigate via React Router, or open in new tab when Cmd/Ctrl is held */
  const navigateTo = useCallback(
    (path: string) => (e?: { metaKey?: boolean; ctrlKey?: boolean }) => {
      if (e && (e.metaKey || e.ctrlKey)) {
        window.open(path, '_blank', 'noopener,noreferrer')
      } else {
        void navigate(path)
      }
    },
    [navigate],
  )

  // Determine active menu item based on current route
  const activeEntryId = pathname.startsWith('/user-accounts')
    ? 'users'
    : pathname.startsWith('/tenants')
      ? 'tenants'
      : pathname.startsWith('/feature-toggles')
        ? 'feature-toggles'
        : 'dashboard'

  return (
    <SqwLayout
      header={{
        logo: (
          <button
            type="button"
            onClick={navigateTo(ROUTES.DASHBOARD)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, height: '100%', display: 'flex', alignItems: 'center' }}
          >
            <img
              src="https://d3nnb1hxumbr0v.cloudfront.net/images/logos2024Relaunch/HRW_Logo_ohne_Claim_Farbe.png"
              alt="HRWorks Logo"
            />
          </button>
        ),
        leftItems: breadcrumbs.length > 0 ? (
          <Breadcrumb>
            {breadcrumbs.map((item, index) => {
              const isLast = index === breadcrumbs.length - 1
              return (
                <BreadcrumbItem
                  key={item.path}
                  onClick={isLast ? undefined : navigateTo(item.path)}
                >
                  {item.label}
                </BreadcrumbItem>
              )
            })}
          </Breadcrumb>
        ) : undefined,
        rightItems: <UserMenu />,
      }}
      sidebar={{
        children: (
          <nav aria-label={formatMessage({ id: 'nav.primary.aria' })}>
            <CollapsibleMenu activeEntryId={activeEntryId}>
              <CollapsibleMenuEntry
                icon={<StreamlineIcon name="hierarchy-9" />}
                text={formatMessage({ id: 'nav.dashboard' })}
                id="dashboard"
                onClick={navigateTo(ROUTES.DASHBOARD)}
              />
              <CollapsibleMenuEntry
                icon={<StreamlineIcon name="building-1" />}
                text={formatMessage({ id: 'nav.tenants' })}
                id="tenants"
                onClick={navigateTo(ROUTES.TENANTS)}
              />
              <CollapsibleMenuEntry
                icon={<StreamlineIcon name="personnel-administration" />}
                text={formatMessage({ id: 'nav.users' })}
                id="users"
                onClick={navigateTo(ROUTES.USERS)}
              />
              <CollapsibleMenuEntry
                icon={<StreamlineIcon name="list-to-do" />}
                text={formatMessage({ id: 'nav.featureToggles' })}
                id="feature-toggles"
                onClick={navigateTo(ROUTES.FEATURE_TOGGLES)}
              />
            </CollapsibleMenu>
          </nav>
        ),
      }}
      content={{
        children: (
          <main>
            <Outlet />
          </main>
        ),
      }}
    />
  )
}
